import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseClient } from '../../utils/supabase';
import { fmtDay } from '../LeadImport/ui';

/** `lead_source_vendor`: who sells the source and how its deliveries are dated. Hidden from reps. */
export interface SourceVendor {
  vendor_name: string | null;
  contact_name: string | null;
  sender_email: string | null;
  received_by: string | null;
  batch_date_rule: string | null;
  default_batch_date: string | null;
  history: string | null;
}

const EMPTY: SourceVendor = {
  vendor_name: null, contact_name: null, sender_email: null, received_by: null,
  batch_date_rule: null, default_batch_date: null, history: null,
};

const FIELDS: { key: keyof SourceVendor; label: string; wide?: boolean; date?: boolean }[] = [
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'contact_name', label: 'Contact' },
  { key: 'sender_email', label: 'Sends from' },
  { key: 'received_by', label: 'Arrives at' },
  { key: 'default_batch_date', label: 'Date when a file has none', date: true },
  { key: 'batch_date_rule', label: 'How a delivery is dated', wide: true },
  { key: 'history', label: 'Past deliveries', wide: true },
];

async function saveVendor(source: number, vendor: SourceVendor): Promise<SourceVendor> {
  const { data, error } = await (supabaseClient as any).rpc('lead_import_source_vendor_save', { p_source: source, p_vendor: vendor });
  if (error) throw error;
  return data as SourceVendor;
}

export default function SourceVendorCard({ source, vendor }: { source: number; vendor: SourceVendor | null }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SourceVendor | null>(null);
  const save = useMutation({
    mutationFn: (v: SourceVendor) => saveVendor(source, v),
    onSuccess: () => {
      setDraft(null);
      return queryClient.invalidateQueries({ queryKey: ['lead-source-import-report', source] });
    },
  });
  const known = vendor != null && FIELDS.some((f) => vendor[f.key]);

  return (
    <section className="rep-data-card source-vendor" data-testid="source-vendor" aria-label="Vendor">
      <div className="rep-card-title">
        <b>Vendor</b>
        <span>Only people with the Lead import permission see this.</span>
        {!draft && (
          <button type="button" className="source-vendor-edit" onClick={() => { save.reset(); setDraft({ ...EMPTY, ...vendor }); }}>
            {known ? 'Edit' : 'Add vendor'}
          </button>
        )}
      </div>
      {draft ? (
        <form className="source-vendor-grid" onSubmit={(e) => { e.preventDefault(); save.mutate(draft); }}>
          {FIELDS.map((f) => (
            <label key={f.key} className={f.wide ? 'wide' : undefined}>
              <span>{f.label}</span>
              {f.key === 'history' ? (
                <textarea rows={3} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
              ) : (
                <input type={f.date ? 'date' : 'text'} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} />
              )}
            </label>
          ))}
          {save.error && <p className="wide source-vendor-error" role="alert">{(save.error as Error).message}</p>}
          <div className="wide source-vendor-actions">
            <button type="button" onClick={() => setDraft(null)} disabled={save.isPending}>Cancel</button>
            <button type="submit" className="primary" disabled={save.isPending}>{save.isPending ? 'Saving...' : 'Save vendor'}</button>
          </div>
        </form>
      ) : known ? (
        <dl className="source-vendor-grid">
          {FIELDS.filter((f) => vendor![f.key]).map((f) => (
            <div key={f.key} className={f.wide ? 'wide' : undefined}>
              <dt>{f.label}</dt>
              <dd>{f.date ? fmtDay(vendor![f.key]) : vendor![f.key]}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="source-vendor-none">No vendor recorded. Add who sells this source and where its files arrive, so past files can be dated.</p>
      )}
    </section>
  );
}
